var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Flex, Form, InputNumber, Radio, Select, Typography, WeekdayPicker, } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import { RRule } from 'rrule';
import { getByWeekday, intervalToFrequency, presetToCustomState, recurrenceRuleToFrequency, } from './utils';
var MomentDatePicker = DatePicker.generatePicker(momentGenerateConfig);
export var RecurrenceDropdown = function (_a) {
    var onChange = _a.onChange, dateStart = _a.dateStart, _b = _a.initialRRule, initialRRule = _b === void 0 ? 'DO_NOT_REPEAT' : _b, disabled = _a.disabled, _c = _a.abtUntilDate, abtUntilDate = _c === void 0 ? null : _c, _d = _a.hasAbtConflict, hasAbtConflict = _d === void 0 ? false : _d, _e = _a.recurrenceEndsBeforeStart, recurrenceEndsBeforeStart = _e === void 0 ? false : _e;
    var _f = useTranslation('meetingRooms'), t = _f.t, i18n = _f.i18n;
    var localizedAbtUntilDate = useMemo(function () {
        if (!abtUntilDate)
            return '';
        return abtUntilDate.clone().locale(i18n.language).format('LL');
    }, [abtUntilDate, i18n.language]);
    var _g = __read(useState(initialRRule), 2), recurrenceRule = _g[0], setRecurrenceRule = _g[1];
    // Under ABT, the rrule is always emitted as CUSTOM (so endDate is editable),
    // but the dropdown should still reflect the preset the user picked. This
    // state holds that label; null means "show whatever recurrenceRule says".
    var _h = __read(useState(function () {
        return abtUntilDate &&
            initialRRule !== 'CUSTOM' &&
            initialRRule !== 'DO_NOT_REPEAT'
            ? initialRRule
            : null;
    }), 2), presetUnderAbt = _h[0], setPresetUnderAbt = _h[1];
    var _j = __read(useState('WEEK'), 2), interval = _j[0], setInterval = _j[1];
    var _k = __read(useState(1), 2), intervalValue = _k[0], setIntervalValue = _k[1];
    var _l = __read(useState(function () {
        return abtUntilDate ? 'onDate' : 'never';
    }), 2), endOption = _l[0], setEndOption = _l[1];
    var _m = __read(useState(function () {
        return abtUntilDate ? abtUntilDate.clone() : null;
    }), 2), endDate = _m[0], setEndDate = _m[1];
    var _o = __read(useState(1), 2), occurrenceCount = _o[0], setOccurrenceCount = _o[1];
    var _p = __read(useState([]), 2), selectedWeekdays = _p[0], setSelectedWeekdays = _p[1];
    // Bumped on every preset→CUSTOM conversion. Used as a key on WeekdayPicker,
    // which is uncontrolled internally (UI-Kit reads selectedDays only at mount),
    // so we force a remount whenever we set selectedWeekdays externally.
    var _q = __read(useState(0), 2), conversionId = _q[0], setConversionId = _q[1];
    // Rewrites a non-CUSTOM preset (e.g. DAILY, WEEKDAY) into the equivalent
    // CUSTOM-panel state with endDate pinned to abtUntilDate. The dropdown then
    // shows "Custom..." with the matching pattern prefilled, giving the user an
    // editable end date instead of an invisible cap.
    var convertPresetToCustom = function (rule, until) {
        var custom = presetToCustomState(rule, dateStart);
        if (!custom)
            return false;
        setInterval(custom.interval);
        setIntervalValue(custom.intervalValue);
        setSelectedWeekdays(custom.selectedWeekdays);
        setEndOption('onDate');
        setEndDate(until.clone());
        setRecurrenceRule('CUSTOM');
        setConversionId(function (id) { return id + 1; });
        return true;
    };
    // If ABT clears, drop the preset label so the dropdown reflects the
    // underlying recurrenceRule again.
    useEffect(function () {
        if (!abtUntilDate && presetUnderAbt !== null) {
            setPresetUnderAbt(null);
        }
    }, [abtUntilDate, presetUnderAbt]);
    var handleRecurrenceRuleChange = function (value) {
        if (abtUntilDate &&
            value !== 'CUSTOM' &&
            value !== 'DO_NOT_REPEAT' &&
            convertPresetToCustom(value, abtUntilDate)) {
            setPresetUnderAbt(value);
            return;
        }
        setPresetUnderAbt(null);
        setRecurrenceRule(value);
    };
    // Compute rrule based on current state (pure computation, no side effects)
    var rrule = useMemo(function () {
        if (recurrenceRule === 'DO_NOT_REPEAT') {
            return null;
        }
        var freq = recurrenceRule === 'CUSTOM'
            ? intervalToFrequency(interval)
            : recurrenceRuleToFrequency(recurrenceRule);
        if (!freq) {
            return null;
        }
        var byweekday = getByWeekday(recurrenceRule, selectedWeekdays, dateStart);
        var options = {
            freq: freq,
            interval: intervalValue,
        };
        if (byweekday && byweekday.length > 0) {
            options.byweekday = byweekday;
        }
        if (recurrenceRule === 'CUSTOM') {
            if (endOption === 'onDate' && endDate) {
                options.until = endDate.toDate();
            }
            else if (endOption === 'afterOccurrences') {
                options.count = occurrenceCount;
            }
        }
        return new RRule(options);
    }, [
        recurrenceRule,
        dateStart,
        interval,
        intervalValue,
        selectedWeekdays,
        endOption,
        endDate,
        occurrenceCount,
    ]);
    // Store onChange in ref to avoid infinite loops from function reference changes
    var onChangeRef = useRef(onChange);
    useEffect(function () {
        onChangeRef.current = onChange;
    }, [onChange]);
    var isFirstRender = useRef(true);
    // Notify parent when rrule changes (side effect in useEffect)
    useEffect(function () {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (onChangeRef.current) {
            onChangeRef.current(rrule);
        }
    }, [rrule]);
    // Memoize dayName based on the date value, not the Moment object reference
    var dayName = useMemo(function () { return dateStart.format('dddd'); }, [dateStart]);
    var recurrenceOptions = useMemo(function () {
        return [
            {
                value: 'DO_NOT_REPEAT',
                label: t('eventDetails.recurrence.options.does_not_repeat'),
            },
            { value: 'DAILY', label: t('eventDetails.recurrence.options.daily') },
            { value: 'WEEKLY', label: t('eventDetails.recurrence.options.weekly') },
            {
                value: 'MONTHLY',
                label: t('eventDetails.recurrence.options.monthly'),
            },
            {
                value: 'WEEKDAY',
                label: t('eventDetails.recurrence.options.weekday'),
            },
            { value: 'MOWEFR', label: t('eventDetails.recurrence.options.mowefr') },
            { value: 'TUTH', label: t('eventDetails.recurrence.options.tuth') },
            {
                value: 'WEEKLY_ON_DAY',
                label: t('eventDetails.recurrence.options.weekly_on_day', {
                    dayName: dayName,
                }),
            },
            {
                value: 'MONTHLY_ON_DAY',
                label: t('eventDetails.recurrence.options.monthly_on_day'),
            },
            { value: 'CUSTOM', label: t('eventDetails.recurrence.options.custom') },
        ];
    }, [t, dayName]);
    var intervalOptions = useMemo(function () {
        return [
            { value: 'DAY', label: t('eventDetails.recurrence.intervals.day') },
            { value: 'WEEK', label: t('eventDetails.recurrence.intervals.week') },
            {
                value: 'MONTHLY',
                label: t('eventDetails.recurrence.intervals.month'),
            },
        ];
    }, [t]);
    return (_jsxs(Flex, { vertical: true, gap: 8, children: [_jsx(Form.Item, { label: t('eventDetails.recurrence.label'), layout: "vertical", style: { marginBottom: 0 }, children: _jsx(Select, { value: presetUnderAbt !== null && presetUnderAbt !== void 0 ? presetUnderAbt : recurrenceRule, onChange: handleRecurrenceRuleChange, options: recurrenceOptions, disabled: disabled, status: hasAbtConflict || recurrenceEndsBeforeStart ? 'error' : undefined }) }), abtUntilDate && recurrenceRule !== 'DO_NOT_REPEAT' && (_jsx(Typography.Text, { type: "secondary", children: t('eventDetails.recurrence.advanced_booking_threshold_subtext', {
                    date: localizedAbtUntilDate,
                }) })), recurrenceEndsBeforeStart && recurrenceRule !== 'DO_NOT_REPEAT' && (_jsx(Typography.Text, { type: "danger", children: t('eventDetails.recurrence.ends_before_start') })), hasAbtConflict &&
                !recurrenceEndsBeforeStart &&
                recurrenceRule !== 'DO_NOT_REPEAT' && (_jsx(Typography.Text, { type: "danger", children: t('eventDetails.recurrence.advanced_booking_threshold_conflict') })), recurrenceRule === 'CUSTOM' && (_jsxs(_Fragment, { children: [_jsx(Form.Item, { label: t('eventDetails.recurrence.repeat_every'), layout: "vertical", style: { marginBottom: 0 }, children: _jsxs(Flex, { gap: 4, children: [_jsx(InputNumber, { step: 1, value: intervalValue, onChange: function (value) {
                                        setIntervalValue(value || 1);
                                        setPresetUnderAbt(null);
                                    } }), _jsx(Select, { value: interval, onChange: function (value) {
                                        setInterval(value);
                                        setPresetUnderAbt(null);
                                    }, options: intervalOptions })] }) }), _jsx(Form.Item, { label: t('eventDetails.recurrence.repeat_on'), layout: "vertical", style: { marginBottom: 0 }, children: _jsx(WeekdayPicker, { selectedDays: selectedWeekdays, onChange: function (days) {
                                setSelectedWeekdays(days);
                                setPresetUnderAbt(null);
                            } }, conversionId) }), _jsx(Form.Item, { label: t('eventDetails.recurrence.ends'), layout: "vertical", style: { marginBottom: 0 }, children: _jsxs(Radio.Group, { onChange: function (e) {
                                setEndOption(e.target.value);
                                setPresetUnderAbt(null);
                            }, value: endOption, style: { display: 'flex', flexDirection: 'column', gap: 8 }, children: [!abtUntilDate && (_jsx(Radio, { value: "never", children: t('eventDetails.recurrence.end_options.never') })), _jsxs(Flex, { gap: "small", align: "center", children: [_jsx(Radio, { value: "onDate", children: t('eventDetails.recurrence.end_options.on') }), _jsx(MomentDatePicker, { value: endDate, onChange: function (date) {
                                                setEndDate(date);
                                                setEndOption('onDate');
                                                setPresetUnderAbt(null);
                                            }, allowClear: false, minDate: dateStart })] }), _jsxs(Flex, { gap: "small", align: "center", children: [_jsx(Radio, { value: "afterOccurrences", children: t('eventDetails.recurrence.end_options.after') }), _jsx(InputNumber, { step: 1, min: 1, value: occurrenceCount, onChange: function (value) {
                                                setOccurrenceCount(value || 1);
                                                setEndOption('afterOccurrences');
                                                setPresetUnderAbt(null);
                                            } }), _jsx(Typography.Text, { children: t('eventDetails.recurrence.end_options.occurrences') })] })] }) })] }))] }));
};
